import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addItem } from "../store/cartSlice.js";
import { fetchProducts } from "../store/productsSlice.js";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.items || []);
  const user = useSelector((s) => s.auth.user);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!products.length) dispatch(fetchProducts());
  }, [dispatch, products.length]);

  const product = products.find((p) => p.id === id);

  if (!products.length) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3 text-muted">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-5 text-center">
        <i className="bi bi-exclamation-circle display-4 text-muted d-block mb-3" />
        <h4>Product not found</h4>
        <button className="btn btn-primary mt-3 rounded-pill px-4" onClick={() => navigate("/perfumes")}>
          Back to Collections
        </button>
      </div>
    );
  }

  // Support both old single image and new images array
  const images = product.images?.length ? product.images : product.image ? [product.image] : ["/slider/1.jpeg"];
  const discount = product.oldPrice
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    if (!user) { toast.info("Please sign in to purchase"); navigate("/signin"); return; }
    dispatch(addItem({ id: product.id, name: product.name, price: product.price, image: images[0] }));
    toast.success("Added to cart!");
  };

  const handleBuyNow = () => {
    if (!user) { toast.info("Please sign in to purchase"); navigate("/signin"); return; }
    dispatch(addItem({ id: product.id, name: product.name, price: product.price, image: images[0] }));
    navigate("/cart");
  };

  return (
    <div style={{ background: "#f1f3f6", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <div className="bg-white border-bottom py-2 px-3">
        <div className="container">
          <nav className="small text-muted">
            <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>Home</span>
            <span className="mx-2">/</span>
            <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => navigate("/perfumes")}>Collections</span>
            <span className="mx-2">/</span>
            <span>{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container py-4">
        <div className="row g-0">

          {/* LEFT — Image Gallery */}
          <div className="col-lg-5">
            <div className="bg-white rounded-3 p-3 shadow-sm" style={{ position: "sticky", top: 80 }}>

              {/* Main Image */}
              <div
                className="position-relative overflow-hidden rounded-3 mb-3 product-detail-img"
                style={{ height: 380, background: "#f8f9fa", cursor: zoom ? "zoom-out" : "zoom-in" }}
                onClick={() => setZoom(!zoom)}
              >
                <img
                  src={images[activeImg]}
                  alt={product.name}
                  className="w-100 h-100"
                  style={{
                    objectFit: zoom ? "contain" : "cover",
                    transition: "all 0.3s ease",
                    transform: zoom ? "scale(1.5)" : "scale(1)",
                  }}
                />
                {discount > 0 && (
                  <span className="position-absolute top-0 start-0 m-3 badge bg-danger rounded-pill px-3 py-2">
                    {discount}% OFF
                  </span>
                )}
                <span className="position-absolute bottom-0 end-0 m-3 badge bg-dark small">
                  <i className="bi bi-zoom-in me-1"></i>{zoom ? "Click to zoom out" : "Click to zoom in"}
                </span>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="d-flex gap-2 flex-wrap justify-content-center">
                  {images.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => { setActiveImg(i); setZoom(false); }}
                      className="rounded-2 overflow-hidden"
                      style={{
                        width: 72, height: 72, cursor: "pointer",
                        border: activeImg === i ? "2px solid #2874f0" : "2px solid #e0e0e0",
                        transition: "border 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <img src={img} alt={`thumb-${i}`} className="w-100 h-100" style={{ objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Product Details */}
          <div className="col-lg-7 ps-lg-4 mt-3 mt-lg-0">
            <div className="bg-white rounded-3 p-4 shadow-sm mb-3">

              {/* Title & Type */}
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <h2 className="fw-bold mb-0" style={{ fontSize: "1.5rem" }}>{product.name}</h2>
                <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 flex-shrink-0">
                  {product.type || "Premium"}
                </span>
              </div>

              {/* Rating row */}
              <div className="d-flex align-items-center gap-3 mb-3">
                <span className="badge bg-success px-2 py-1">
                  <i className="bi bi-star-fill me-1"></i>4.9
                </span>
                <span className="text-muted small">Verified Purchase</span>
                <span className="badge bg-light text-dark border">{product.gender || "Unisex"}</span>
              </div>

              {/* Price */}
              <div className="border-top border-bottom py-3 mb-3">
                <div className="d-flex align-items-baseline gap-3 flex-wrap">
                  <span className="fw-bold" style={{ fontSize: "2rem", color: "#388e3c" }}>
                    ₹{Number(product.price).toLocaleString()}
                  </span>
                  {product.oldPrice && (
                    <>
                      <span className="text-muted text-decoration-line-through fs-5">
                        ₹{Number(product.oldPrice).toLocaleString()}
                      </span>
                      <span className="text-danger fw-bold fs-5">{discount}% off</span>
                    </>
                  )}
                </div>
                <div className="small text-muted mt-1">Inclusive of all taxes • Free delivery</div>
              </div>

              {/* Key Specs */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3">Product Highlights</h6>
                <div className="row g-2">
                  {[
                    { icon: "droplet-fill", color: "text-primary", label: "Volume", value: product.volume || "50ml" },
                    { icon: "clock-fill", color: "text-success", label: "Longevity", value: product.longevity || "12+ Hours" },
                    { icon: "wind", color: "text-info", label: "Sillage", value: product.sillage || "High" },
                    { icon: "thermometer-half", color: "text-warning", label: "Season", value: product.season || "All Year" },
                  ].map((spec) => (
                    <div key={spec.label} className="col-6">
                      <div className="d-flex align-items-center gap-2 p-2 bg-light rounded-3">
                        <i className={`bi bi-${spec.icon} ${spec.color}`}></i>
                        <div>
                          <div className="small text-muted" style={{ fontSize: "0.7rem" }}>{spec.label}</div>
                          <div className="fw-semibold small">{spec.value}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              {product.description && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Description</h6>
                  <p className="text-muted" style={{ lineHeight: 1.7 }}>{product.description}</p>
                </div>
              )}

              {/* Delivery Info */}
              <div className="d-flex align-items-center gap-3 mb-4 p-3 bg-light rounded-3">
                <i className="bi bi-truck text-success fs-5"></i>
                <div>
                  <div className="fw-semibold small">Free Delivery</div>
                  <div className="text-muted small">Delivered in 5–7 business days</div>
                </div>
                <div className="ms-auto">
                  <i className="bi bi-shield-check text-primary fs-5"></i>
                </div>
                <div>
                  <div className="fw-semibold small">100% Authentic</div>
                  <div className="text-muted small">Genuine product</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <button
                  className="btn btn-warning fw-bold flex-fill py-3 rounded-3"
                  style={{ fontSize: "1rem" }}
                  onClick={handleAddToCart}
                >
                  <i className="bi bi-cart-plus-fill me-2"></i>Add to Cart
                </button>
                <button
                  className="btn fw-bold flex-fill py-3 rounded-3 text-white"
                  style={{ background: "#fb641b", fontSize: "1rem" }}
                  onClick={handleBuyNow}
                >
                  <i className="bi bi-lightning-fill me-2"></i>Buy Now
                </button>
              </div>
            </div>

            {/* All Images Preview */}
            {images.length > 1 && (
              <div className="bg-white rounded-3 p-4 shadow-sm">
                <h6 className="fw-bold mb-3">All Product Images ({images.length})</h6>
                <div className="row g-2">
                  {images.map((img, i) => (
                    <div key={i} className="col-4 col-md-3">
                      <div
                        className="rounded-3 overflow-hidden"
                        style={{ height: 100, cursor: "pointer", border: activeImg === i ? "2px solid #2874f0" : "2px solid transparent" }}
                        onClick={() => { setActiveImg(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >
                        <img src={img} alt={`view-${i}`} className="w-100 h-100" style={{ objectFit: "cover" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
